/**
 * Active Wildland Fires layer (Phase E.2.1) — the first operator-visible Fire
 * Risk layer.
 *
 * Fetches /api/active-fires for the current bbox (debounced) and renders each fire
 * via a Cesium GeoJsonDataSource: a stage-coloured perimeter polygon when the API
 * matched one, plus — icon one-swing #564 (§C.1) — the EXACT-ACT fire DISC billboard
 * at the incident point (a RESPONSE-LEVEL-filled disc with the white `fire` glyph
 * knocked out). This RETIRES the prior bare stage-tinted flame glyph + the separate
 * managed ACK-STEEL ring + the separate red RL≥3 urgency GLOW-halo (three entities →
 * one disc: urgency = disc FILL/L4-pulse, worked = the steel disc). The disc FILL is
 * the RISK-GATED Response Level (`oc_fire_response_level`, R2 ladder — NOT auto-L4);
 * STAGE-of-control now survives on the ambient stage-coloured PERIMETER + the panel
 * badge + the disc's live/resolved opacity (styles/fireStages still drives all three,
 * plus the perimeter's hectare extent). The disc SIZE orders by Response Level, not
 * hectares (founder ruling 2026-07-06 §5 — fireDiscSizePx). Founder LOD ruling
 * (fix/wildfire-oc-render-lod): the country view shows ONLY urgent OC fires (RL≥3);
 * zooming to provincial reveals ALL OC fires (fireLodGate → distanceDisplayCondition).
 * The always-visible urgent fires still declutter on the shared #410 country-zoom curve. Per the
 * founder motion-budget revision (2026-06-05, §3B.8) + the icon-council R4, the ONLY
 * fire motion is the unacked-L4 disc fill-breathe; L3/L2/L1 discs are static.
 *
 * Like ProvincialBoundariesLayer, MapView owns the Cesium viewer, so this is an
 * imperative installer rather than a React component. MapView calls
 * `requestRefresh` on camera moves, `setVisible` on the Fire Risk toggle, and
 * `pick` from its central click handler to route a fire selection to the side
 * panel.
 *
 * Defensive patterns carried from E.1.7+: GeoJsonDataSource natively handles
 * Polygon AND MultiPolygon; an unmapped stage renders grey + WARN (never
 * dropped); cross-source dedup happens at the API tier, not here.
 */
import * as Cesium from 'cesium';
import type { ActiveFireFeature, ActiveFireFeatureCollection } from '@tsr/shared-types';
import { isFireStage, escalationSafeResponseLevel } from '@tsr/shared-types';
import { fetchActiveFires, type ActiveFireFetchOpts, type BboxQuery } from '../api';
import { fireRenderStyle, isOcFul, type FireRenderStyle } from '../styles/fireStages';
import {
  ACK_STEEL,
  ACK_STEEL_ALPHA,
  anomalyGate,
  anomalyLevelBillboardColor,
  discCountryScale,
} from './anomalyMarker';
import { hotspotLevelStyle, isResponseLevel, RESPONSE_LEVEL_COLORS } from '../styles/responseLevel';
import { renderDiscGlyph } from '../discGlyphComposite';
import { ownershipRing } from '../styles/ownershipCue';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { isWithinCanadaScope } from '../canadaScope';
import type { OcFulFire } from './coLocatedOcFulSuppression';

const REFRESH_DEBOUNCE_MS = 300;
const UUID_RE = /^[0-9a-f-]{36}$/i;

/**
 * The OC-FUL fires in a collection (Phase 5a; council §A) — id + the `centroid`
 * position. A fire is OC-FUL iff `isOcFul(...)` (stage OC AND response_type FUL —
 * the SAME authoritative predicate, formerly the reserved-flame selector). Phase 5c
 * made all stages render the CAHS fire glyph, so this is no longer "the flame set";
 * it stays the OC-FUL set MapView feeds to the hotspot layer to suppress a redundant
 * co-located dot (§G precision guard: ONLY an OC-FUL fire suppresses). Pure +
 * exported for unit tests.
 */
export function ocFulFiresFromCollection(fc: ActiveFireFeatureCollection): OcFulFire[] {
  const fires: OcFulFire[] = [];
  for (const f of fc.features) {
    if (isOcFul(f.properties.stage_of_control_status, f.properties.response_type ?? null)) {
      const [lon, lat] = f.properties.centroid;
      fires.push({ id: f.id, lon, lat });
    }
  }
  return fires;
}

/**
 * Managed-ring colour (founder ruling 2026-06-22 + Colour Law 1 ACK-STEEL token —
 * "we're managing it"). The fire feature carries NO ack workstate (anomaly_workstate
 * has no 'fire' source_type and STEP 1 surfaces `owned_incident` only), so the single
 * STANDING steel ring is correct — we deliberately do NOT invent an ack field to
 * drive an URGENT-RED→ACK-STEEL transition. (If a future slice surfaces a real fire
 * ack workstate, route it through the anomalyMarker isAcknowledged chokepoint here
 * rather than re-deriving a hue.)
 */
const MANAGED_RING_COLOR = ACK_STEEL;

/** The Phase 5b owned-incident map channel for a fire. Ownership is a UNIFORM ring,
 *  NOT a per-stage symbol (founder ruling 2026-06-22). */
export type OwnedFireChannel = { kind: 'none' } | { kind: 'ring'; color: string };

/**
 * Resolve the response-phase ring treatment for a fire. ADDITIVE — it NEVER replaces
 * the base stage read:
 *   - not in response → { kind: 'none' }  (renders byte-identically to the base flame)
 *   - in response     → { kind: 'ring' }  (a static ACK-STEEL "managed" ring on the fire
 *                                          marker, any stage)
 *
 * R1/#477 CONSOLIDATION: the ACK-STEEL ring now MEANS the 'response' lifecycle phase
 * (the active "Response" phase / IAP, App A #68), NOT mere ownership. It is RE-SOURCED
 * from the old `owned_incident != null` gate to `incident_lifecycle_state === 'response'`
 * — so the ring is unambiguous (one meaning) and the earlier phases (detected/confirmed/
 * owned) carry their cues elsewhere (the hotspot chip plate/glyph/keyline; a fire flame
 * has no chip, so on fires the visible lifecycle cues are this ring + the deactivated
 * suppression). One uniform ring regardless of stage (founder ruling 2026-06-22). It
 * composes with the base wildfire symbol as a third orthogonal channel (shape = fire ·
 * ring = response · colour = stage). Single-tenant, so NO agency / permission check
 * (#190/#315 deferred). Pure + exported so the decision is unit-locked.
 */
export function ownedFireChannel(feature: ActiveFireFeature): OwnedFireChannel {
  return feature.properties.incident_lifecycle_state === 'response'
    ? { kind: 'ring', color: MANAGED_RING_COLOR }
    : { kind: 'none' };
}

/**
 * The EXACT-ACT DISC treatment for a wildfire marker (icon one-swing #564, §C.1). The OC/
 * reported-wildfire family now renders as ONE disc billboard — a RESPONSE-LEVEL-filled disc
 * with the white `fire` glyph knocked out — RETIRING the prior three-entity render (bare
 * stage-tinted flame + separate managed ACK-STEEL ring + separate red urgency GLOW-halo). This
 * pure decision resolves that disc's fill + pulse + worked state (the alpha's stage-opacity
 * multiply stays at the call site, which has the FireRenderStyle):
 *   - fill = the RISK-GATED Response Level (`oc_fire_response_level`, R2 council ladder — OC
 *     floors at RL2, escalators raise; NOT auto-L4), via RESPONSE_LEVEL_COLORS; a non-OC stage
 *     (BH/UC/OUT) has a null RL → muted L1 slate (never dropped). STAGE-of-control no longer
 *     tints the disc face — it survives on the ambient stage-coloured PERIMETER polygon + the
 *     side-panel FireStageBadge (fireStages.ts is untouched: it still drives the perimeter,
 *     the hectare sizing, and the live/resolved opacity).
 *   - worked (ACK-STEEL disc, §C.1-2 "worked/owned = the steel disc, no ring") = an oc_fire ack
 *     workstate (`work_acknowledged_at`) OR the 'response' lifecycle phase (the old managed-ring
 *     trigger, reused via {@link ownedFireChannel}).
 *   - pulse = the unacked L4 is the single pulsing class (R4) → a fill-breathe on billboard.color;
 *     L3 static amber, replacing the retired RL≥3 glow-halo.
 * Pure + exported so the fill/pulse/worked decision is unit-locked (mirrors the retired
 * ownedRingFireIds/urgentPulseFireIds seams, now repointed at this).
 */
/**
 * #445-ws2 — the EFFECTIVE OC-fire Response Level that drives the disc (fill/size/pulse):
 * the owning IC's operator pin layered OVER the derived OC ladder via the ESCALATION-SAFE
 * max — an IC escalation enlarges/reddens the disc; a de-escalation can NEVER shrink it
 * below the live derived (the guard, a life-safety surface). It applies ONLY to an OC fire
 * (derived non-null): a non-OC fire (BH/UC/OUT → null derived) keeps its FROZEN muted glyph
 * regardless of any incident pin, which surfaces instead on the incident detail + SITREP.
 * `operator_response_level` is null for a non-owned fire ⇒ pure derived (icons unchanged).
 */
export function effectiveFireResponseLevel(feature: ActiveFireFeature): number | null {
  const derived = feature.properties.oc_fire_response_level ?? null;
  if (derived == null) return null;
  return (
    escalationSafeResponseLevel(feature.properties.operator_response_level ?? null, derived) ??
    derived
  );
}

export function fireDiscTreatment(feature: ActiveFireFeature): {
  worked: boolean;
  pulse: boolean;
  fill: string;
  baseAlpha: number;
} {
  const rl = effectiveFireResponseLevel(feature);
  const worked =
    feature.properties.work_acknowledged_at != null || ownedFireChannel(feature).kind === 'ring';
  const levelBase = hotspotLevelStyle(rl);
  const pulse = levelBase.level === 4 && !worked;
  const fill = worked
    ? ACK_STEEL
    : isResponseLevel(rl)
      ? RESPONSE_LEVEL_COLORS[rl]
      : RESPONSE_LEVEL_COLORS[1];
  const baseAlpha = worked ? ACK_STEEL_ALPHA : levelBase.alpha;
  return { worked, pulse, fill, baseAlpha };
}

/**
 * The EXACT-ACT fire disc's on-screen diameter (px) at a given WALL scale (#132; 1 =
 * operator COP). Founder ruling 2026-07-06 §5 (findings 2 & 3): marker size orders by
 * RESPONSE LEVEL, not hectares — an OC fire's disc rides the SAME RL ladder every other
 * disc family uses (hotspotLevelStyle.baseSize: L1 12 · L2 16 · L3 24 · L4 34), so a
 * lower-RL fire can never out-size a red RL4 marker (finding 2) and an RL4 marker of any
 * family is never smaller than a lower-RL fire at equal zoom (finding 3 cross-family
 * coherence). This RESTORES icon-council R1 ("size = the redundant level cue"), which the
 * #564 one-swing broke by leaving the fire disc on its legacy hectare-band sizing. A non-OC
 * stage (BH/UC/OUT → null oc_fire_response_level) falls to the muted L1-ish base, matching
 * its L1-slate fill. Hectares survive as the ambient PERIMETER polygon (fireStages), not
 * the disc face. Pure + exported so the ladder is unit-locked (crossFamilyIconSize.lock).
 */
export function fireDiscSizePx(ocFireResponseLevel: number | null | undefined, scale = 1): number {
  return hotspotLevelStyle(ocFireResponseLevel ?? null).baseSize * scale;
}

/**
 * The fire disc's zoom gate (founder LOD ruling, fix/wildfire-oc-render-lod). At COUNTRY
 * zoom the map shows ONLY urgent OC fires; zooming to PROVINCIAL reveals ALL OC fires —
 * so the national view is not a sea of fire dots, but the operator loses nothing on zoom.
 *
 * Implemented by REUSING the anomaly dot idiom: run the fire's EFFECTIVE OC Response Level
 * (operator pin over derived, escalation-safe) through {@link anomalyGate}. RL ≥
 * URGENT_MIN_RESPONSE_LEVEL (3) → `undefined` (visible at ALL camera distances, the
 * always-on urgent signal); a non-urgent fire (RL 1/2, or a non-OC BH/UC/OUT fire whose
 * derived OC RL is null) → DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M), the SAME
 * provincial-tier threshold the L1/L2 anomaly dots use — so fires and anomaly dots tier in
 * together. Pure + exported so the LOD is unit-locked. (This SUPERSEDES the prior "fires
 * stay visible at every zoom" behaviour — they now tier by urgency instead of merely
 * decluttering via scaleByDistance, which still runs on the always-visible urgent fires.)
 */
export function fireLodGate(
  feature: ActiveFireFeature,
): Cesium.DistanceDisplayCondition | undefined {
  return anomalyGate(hotspotLevelStyle(effectiveFireResponseLevel(feature)));
}

/**
 * Resolve the fire uuid carried by a GeoJsonDataSource entity. Cesium copies the
 * feature's `properties` bag onto every entity it creates (including each
 * sub-polygon of a MultiPolygon, whose own `entity.id` string is a generated
 * GUID), so the property bag is the reliable key; the entity.id string is a
 * fallback for the single-geometry case. Pure + exported for unit tests.
 *
 * Accepts a structural shape (not Cesium.Entity directly) so the test can pass a
 * plain mock; the real Cesium.Entity is assignable to it.
 */
export function readFireId(entity: { id?: unknown; properties?: unknown }): string | null {
  const props = entity.properties as
    | { id?: { getValue?: (time?: unknown) => unknown } }
    | null
    | undefined;
  const idProp = props?.id;
  if (idProp && typeof idProp.getValue === 'function') {
    const v = idProp.getValue();
    if (typeof v === 'string' && v) return v;
  }
  if (typeof entity.id === 'string' && UUID_RE.test(entity.id)) return entity.id;
  return null;
}

export interface ActiveFiresLayerOptions {
  /** Reports the deduped feature count (for the Fire Risk toggle label). */
  onCount?: (n: number) => void;
  /** Current toggle state — used to set ds.show after a fetch completes. */
  getVisible?: () => boolean;
  /** Optional canonical-stage filter passed through to the API. */
  getStages?: () => string[] | undefined;
  /** Optional response_type filter passed through to the API. The Default OC Layer 4
   *  passes undefined (ALL OC postures — Full/Modified/Monitored/not-reported — reach the
   *  client; the LOD governs visibility, #607/#621); the all-stages layer also passes
   *  undefined. A non-undefined value only comes from fixtures/tests. */
  getResponseTypes?: () => string[] | undefined;
  /** Glyph/marker geometry scale (#132 WALL profile). Default 1 (operator COP). */
  getScale?: () => number;
  /** Phase 5a (council §A) — fired after a refresh whenever the rendered OC-FUL fire
   *  set changes (one appeared / departed / moved), so MapView can re-evaluate the
   *  co-located-hotspot suppression. Not fired when unchanged. */
  onOcFulFiresChanged?: () => void;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: ActiveFireFetchOpts,
  ) => Promise<ActiveFireFeatureCollection>;
}

export interface ActiveFiresLayerHandle {
  /** Debounced (300ms) bbox-change refresh. */
  requestRefresh(bbox: BboxQuery): void;
  /** Immediate fetch + render (used on first paint / toggle-on). */
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its fire feature (null if not ours). */
  pick(entity: unknown): ActiveFireFeature | null;
  /** Phase 5a (council §A) — the OC-FUL fires currently rendered (id + centroid).
   *  MapView reads this to suppress a redundant co-located hotspot dot so the OC-FUL
   *  fire is the single clickable marker. Empty before the first fetch. */
  ocFulFires(): OcFulFire[];
  /** Icon one-swing #564 — the fire ids currently rendered WORKED: the ACK-STEEL disc
   *  fill (an oc_fire ack workstate OR the 'response' lifecycle phase, §C.1-2 "worked/
   *  owned = the steel disc, no ring"). Repoints the retired managed-ring seam. Test
   *  seam; empty before the first fetch. */
  ownedRingFireIds(): string[];
  /** Icon one-swing #564 — the fire ids currently rendered with the L4 disc FILL-BREATHE
   *  (the single pulsing class: unacked oc_fire_response_level 4). Repoints the retired
   *  glow-halo seam (was RL≥3 → now L4-only, matching the disc R4 rule); the anti-flood
   *  motion pin. Test seam; empty before the first fetch. */
  urgentPulseFireIds(): string[];
  destroy(): void;
}

export function installActiveFiresLayer(
  viewer: Cesium.Viewer,
  opts: ActiveFiresLayerOptions = {},
): ActiveFiresLayerHandle {
  const fetcher = opts.fetcher ?? fetchActiveFires;
  const ds = new Cesium.GeoJsonDataSource('active-fires');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, ActiveFireFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load() resolving after a newer one.
  let loadGen = 0;
  // Phase 5a (council §A) — the OC-FUL fires from the latest render, plus a signature
  // to fire onOcFulFiresChanged ONLY when the set actually changes (so a routine
  // camera-move refresh with the same OC-FUL fires doesn't re-render hotspots).
  let currentOcFulFires: OcFulFire[] = [];
  let ocFulSig = '';
  // Phase 5b — the fire ids currently drawn with a managed owned-incident RING (owned
  // fires, any stage). Exposed via ownedRingFireIds() for the pinned 5b test.
  let currentOwnedRingIds: string[] = [];
  // #444 — the OC-fire ids currently drawn with an urgent pulse (RL≥3). Exposed via
  // urgentPulseFireIds() for the pinned anti-flood / motion frame-diff test.
  let currentUrgentPulseIds: string[] = [];

  // Attach the EXACT-ACT DISC billboard for ANY wildfire stage at the fire's incident
  // point (icon one-swing #564, §C.1): ONE billboard — a RESPONSE-LEVEL-filled disc
  // (ACK-STEEL when worked) with the white `fire` glyph knocked out at centre. RETIRES
  // the bare stage-tinted flame + the separate managed ring + the separate red urgency
  // glow-halo (all folded into the disc: urgency = fill/pulse, worked = steel). The disc
  // is CENTER-anchored (not the flame's BOTTOM) — the incident-point centroid is the same
  // anchor the OC-FUL co-location suppression reads (render-position only, unchanged).
  // Polygon entities carry no `position`, so seed one from the feature centroid; the disc
  // then sits at the centroid on top of the stage-coloured perimeter (which survives as
  // the ambient stage read — fill=RL drops stage from the disc FACE, not the map).
  const attachGlyph = (
    entity: Cesium.Entity,
    feature: ActiveFireFeature,
    style: FireRenderStyle,
  ): void => {
    // WALL profile (#132) scales the disc so fires read from across a room; operator
    // COP passes scale 1 (no change). Founder ruling 2026-07-06 §5 (findings 2 & 3):
    // the disc DIAMETER orders by RESPONSE LEVEL — the SAME ladder every disc family
    // uses (fireDiscSizePx → hotspotLevelStyle.baseSize) — NOT the legacy hectare band
    // the #564 one-swing left in place (which let a big low-RL fire out-size a red RL4
    // marker). Hectares survive on the ambient stage-coloured PERIMETER; the escalation
    // size boost is retired because escalation already raises oc_fire_response_level
    // (R2 ladder), so the RL bump IS the escalation cue (no double-count).
    const scale = opts.getScale?.() ?? 1;
    // #445-ws2 — size orders by the EFFECTIVE RL (operator pin over derived, escalation-safe).
    const size = fireDiscSizePx(effectiveFireResponseLevel(feature), scale);
    if (!entity.position) {
      const [lon, lat] = feature.properties.centroid;
      entity.position = new Cesium.ConstantPositionProperty(
        Cesium.Cartesian3.fromDegrees(lon, lat),
      );
    }
    const t = fireDiscTreatment(feature);
    // Live stages at full level alpha; resolved (UC/OUT) further muted via glyphOpacity so
    // they recede — a worked (steel) disc keeps the steel alpha (glyphOpacity not applied).
    const alpha = t.worked ? t.baseAlpha : t.baseAlpha * style.glyphOpacity;
    // OWNERSHIP CUE (feat/ownership-coordination) — a 3-way viewer-relative keyline baked INTO the
    // disc canvas so it scales WITH the disc. ADDITIVE + ORTHOGONAL: it reads owned_incident (NOT
    // fireDiscTreatment) and NEVER changes fill/size/opacity. null (un-owned) ⇒ byte-identical disc.
    const ring = ownershipRing(feature.properties.owned_incident) ?? undefined;
    const image = renderDiscGlyph('fire', { fill: t.fill, ring }) ?? undefined;
    entity.point = undefined;
    entity.billboard = new Cesium.BillboardGraphics({
      image,
      width: size,
      height: size,
      // Unacked L4 (the single pulsing class) → the fill-breathe; else the identity-hue
      // multiplier at the level/steel alpha (the disc image carries the hue).
      color: anomalyLevelBillboardColor(t.pulse, alpha),
      verticalOrigin: Cesium.VerticalOrigin.CENTER,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      // Founder LOD ruling (fix/wildfire-oc-render-lod): country view shows ONLY urgent OC
      // fires (RL≥3); zoom to provincial reveals ALL OC fires. An URGENT fire is visible at
      // every zoom (gate undefined) and still declutters on the #410 curve; a NON-URGENT fire
      // (RL<3, incl. a non-OC null-RL fire) tiers in only at the provincial threshold. See
      // fireLodGate — it reuses the anomaly-dot gate so fires + L1/L2 dots tier in together.
      distanceDisplayCondition: fireLodGate(feature),
      // Cross-family size coherence (founder ruling 2026-07-06 §5.3, NIGHT ruling 1): among
      // the always-visible urgent fires, the disc shrinks on the level-aware #410 declutter
      // curve so at national zoom an RL4 marker of any family never renders smaller than a
      // lower-RL fire. (scaleByDistance sizes; distanceDisplayCondition above gates.)
      scaleByDistance: discCountryScale(
        // #445-ws2 — the country-zoom declutter floor follows the EFFECTIVE RL too.
        hotspotLevelStyle(effectiveFireResponseLevel(feature)).level,
      ),
    });
    // OWNER TAG (feat/ownership-coordination) — the short lead-organization name at REGIONAL zoom+,
    // hidden at country zoom (its own distanceDisplayCondition), so a duty officer reads WHO holds
    // the incident, not just THAT someone does. Fixed pixel size (a label, not the disc). Only when
    // owned; cleared otherwise so a released/deactivated fire drops its tag on the next restyle.
    const ownerName = feature.properties.owned_incident?.lead_organization_name ?? null;
    entity.label = ownerName
      ? new Cesium.LabelGraphics({
          text: ownerName,
          font: '600 12px Inter, system-ui, sans-serif',
          fillColor: Cesium.Color.fromCssColorString('#e2e8f0'),
          showBackground: true,
          backgroundColor: Cesium.Color.fromCssColorString('#0b1020').withAlpha(0.75),
          pixelOffset: new Cesium.Cartesian2(0, -(size / 2) - 4),
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M),
        })
      : undefined;
  };

  const styleEntities = (fc: ActiveFireFeatureCollection): void => {
    const byId = new Map<string, ActiveFireFeature>();
    for (const f of fc.features) byId.set(f.id, f);
    entityToFeature.clear();
    // Icon one-swing #564: the managed ACK-STEEL ring + the RL≥3 red GLOW-halo (both
    // separate entities) are RETIRED — worked→steel DISC fill and unacked-L4→disc
    // fill-breathe fold into the one billboard. These arrays feed the (repointed) test
    // seams: ids rendered WORKED (steel disc) and ids rendered with the L4 fill-breathe.
    const workedIds: string[] = [];
    const pulseIds: string[] = [];
    for (const entity of ds.entities.values) {
      const id = readFireId(entity);
      const feature = id ? byId.get(id) : undefined;
      // R1/#477 — a DEACTIVATED incident leaves the live layer (demobilization →
      // deactivation, §8). Hide the fire marker; the incidents row persists (replayable
      // via the AAR; reignition → status 'open' + lifecycle 'confirmed' (W7/#535 — the last
      // un-commanded phase) re-renders it on the next fetch). The CWFIS active_wildland_fires
      // row is untouched — render-only.
      if (feature?.properties.incident_lifecycle_state === 'deactivated') {
        entity.show = false;
        continue;
      }
      // Canada-scope render guard (finding 1, 2026-07-06 §5.1): hide any fire whose incident
      // point sits outside Canada — the shared dev DB carries ocean-coordinate test fixtures
      // in active_wildland_fires too, and the fire disc has no country-zoom gate. See canadaScope.ts.
      const centroid = feature?.properties.centroid;
      if (centroid && !isWithinCanadaScope(centroid[0], centroid[1])) {
        entity.show = false;
        continue;
      }
      const stage = feature?.properties.stage_of_control_status ?? null;
      if (feature && !isFireStage((stage ?? '').toUpperCase())) {
        // Unmapped/null stage (e.g. ON 'BM') → grey, but never dropped. WARN so
        // the gap is visible without fabricating a canonical value (E.1.7).
        console.warn(
          `[ActiveFiresLayer] fire ${id} has unmapped stage_of_control_status=${JSON.stringify(
            stage,
          )} — rendering grey`,
        );
      }
      // fireStages STILL drives the PERIMETER colour + the live/resolved glyph opacity;
      // the disc FILL is the Response Level and (founder ruling 2026-07-06 §5) the disc
      // SIZE is now the Response-Level ladder too (fireDiscSizePx) — hectares survive only
      // on the ambient perimeter, no longer on the disc face.
      const style = fireRenderStyle(stage, feature?.properties.response_type ?? null);
      const color = Cesium.Color.fromCssColorString(style.color);
      if (entity.polygon) {
        // The stage-coloured PERIMETER stays an ambient field (like CAP polygons) — it is
        // where stage-of-control survives now that the disc face carries the Response Level.
        entity.polygon.material = new Cesium.ColorMaterialProperty(
          color.withAlpha(style.fillAlpha),
        );
        // height 0 (vs clamp-to-ground) so the stroke renders — clamped
        // polygons can't be outlined (same constraint MapView notes for cones).
        entity.polygon.height = new Cesium.ConstantProperty(0);
        entity.polygon.outline = new Cesium.ConstantProperty(true);
        entity.polygon.outlineColor = new Cesium.ConstantProperty(color);
        entity.polygon.outlineWidth = new Cesium.ConstantProperty(style.strokeWidth);
        // Every stage: the EXACT-ACT fire DISC sits at the centroid on top of its perimeter.
        if (feature) attachGlyph(entity, feature, style);
      } else if (feature) {
        // Point fire (any stage): the fire disc replaces the default GeoJSON pin.
        attachGlyph(entity, feature, style);
      }
      if (feature) {
        entityToFeature.set(entity, feature);
        // Icon one-swing #564: worked/pulse now ride the disc fill (steel) / fill-breathe,
        // not separate ring/glow entities. Track the ids for the pinned test seams.
        const t = fireDiscTreatment(feature);
        if (t.worked) workedIds.push(feature.id);
        if (t.pulse) pulseIds.push(feature.id);
      }
    }
    currentOwnedRingIds = workedIds;
    currentUrgentPulseIds = pulseIds;
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal, {
        stages: opts.getStages?.(),
        responseTypes: opts.getResponseTypes?.(),
      });
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      await ds.load(fc, { clampToGround: false });
      if (destroyed || gen !== loadGen) return;
      styleEntities(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
      // Phase 5a — recompute the OC-FUL fire set; notify (so the hotspot layer
      // re-evaluates its co-located suppression) only when it changed. Update the
      // stored set BEFORE notifying so ocFulFires() returns the new set to the
      // re-render. Rounded coords in the signature catch a real centroid move.
      const ocFulFires = ocFulFiresFromCollection(fc);
      const sig = ocFulFires
        .map((f) => `${f.id}@${f.lon.toFixed(4)},${f.lat.toFixed(4)}`)
        .sort()
        .join('|');
      if (sig !== ocFulSig) {
        currentOcFulFires = ocFulFires;
        ocFulSig = sig;
        opts.onOcFulFiresChanged?.();
      }
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchActiveFires failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): ActiveFireFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    ocFulFires(): OcFulFire[] {
      return currentOcFulFires.slice();
    },
    ownedRingFireIds(): string[] {
      return currentOwnedRingIds.slice();
    },
    urgentPulseFireIds(): string[] {
      return currentUrgentPulseIds.slice();
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
